import React, { useMemo } from 'react';
import Button from '../components/Inputs/Button/Button';
import { PencilLine, Trash2 } from "lucide-react";
import GenericListView from '../views/GenericListView';
import FilterContainer from '../containers/FilterContainer';
import ModalContainer from '../containers/ModalContainer';
import useListController from '../hooks/useListController';
import useModalController from '../hooks/useModalController';
import useDialogCopy from '../hooks/useDialogCopy';
import {
    createIndicatorData,
    readIndicatorData,
    updateIndicatorData,
    deleteIndicatorData,
    listIndicatorDataIndicators,
    listIndicatorDataGeoUnits,
    listIndicatorDataSubvalueTypes,
} from '../api/indicatorData';

export default function IndicatorDataPage() {
    const controller = useListController({
        defaults: {
            page: 1,
            size: 10,
            sortBy: 'id',
            sortDir: 'asc',
            filters: { indicator: 'All', geoUnit: 'All', date: 'All', subvalueType: 'All' },
        },
        createFn: createIndicatorData,
        readFn: readIndicatorData,
        updateFn: updateIndicatorData,
        deleteFn: deleteIndicatorData,
    });

    const modalCtl = useModalController({
        create: controller.actions.create,
        update: controller.actions.update,
        remove: controller.actions.remove,
        mapEditValues: (row) => ({
            indicatorId: row.indicatorId ?? row.indicator ?? "",
            geoUnitId: row.geoUnitId ?? row.geoUnit ?? "",
            date: row.date ?? "",
            subvalueType: row.subvalueType ?? "",
            value: row.value ?? "",
            subvalue: row.subvalue ?? "",
        }),
    });

    const columns = useMemo(() => ([
        { key: 'id', label: 'Data ID' },
        { key: 'indicatorName', label: 'Indicator' },
        { key: 'geoUnitName', label: 'Geographic Unit' },
        { key: 'date', label: 'Date ' },
        { key: 'value', label: 'Value' },
        { key: 'subvalueType', label: 'Subvalue Type' },
        { key: 'subvalue', label: 'Subvalue' },
    ]), []);

    const filterConfig = useMemo(() => ([
        { key: 'indicator', label: 'Indicator', type: 'select', allValue: 'All', loadOptions: listIndicatorDataIndicators },
        { key: 'geoUnit', label: 'Geographic Unit', type: 'select', allValue: 'All', loadOptions: listIndicatorDataGeoUnits },
        { key: '', label: 'Date range', type: 'daterange', enforceOrder: true },
        { key: 'subvalueType', label: 'Subvalue Type', type: 'select', allValue: 'All', loadOptions: listIndicatorDataSubvalueTypes },
    ]), []);

    const header = useMemo(() => (
        <FilterContainer
            filters={filterConfig}
            state={controller.state}
            setFilter={controller.actions.setFilter}
            setSearch={controller.actions.setSearch}
            searchBar={false}
        />
    ), [controller.state, controller.actions.setFilter, controller.actions.setSearch, filterConfig]);

    const indicatorDataFields = useMemo(() => ([
        { key: "indicatorId", label: "Indicator", type: "select", required: true, loadOptions: listIndicatorDataIndicators },
        { key: "geoUnitId", label: "Geographic Unit", type: "select", required: true, loadOptions: listIndicatorDataGeoUnits },
        { key: "date", label: "Date", type: "date", required: true },
        {},
        {
            key: "subvalues", leftLabel: "Subvalue Type", rightLabel: "Subvalue", type: "multi", required: true, fullRow: true,
            loadOptions: listIndicatorDataSubvalueTypes,
            inputType: "text", inputPlaceholder: "Number"
        },
    ]), []);

    const actionCell = (row) => (
        <>
            <Button
                variant="warning"
                shape="square"
                leadingIcon={<PencilLine size={18} />}
                onClick={() => modalCtl.actions.openEdit(row)}
            >
                Edit
            </Button>
            <Button
                variant="danger"
                shape="square"
                leadingIcon={<Trash2 size={18} />}
                onClick={() => modalCtl.actions.openDelete(row)}
            >
                Delete
            </Button>
        </>
    );

    const actions = (
        <>
            <Button variant="secondary" shape="pill" onClick={() => {/* hook up uploader */ }}>
                Upload File
            </Button>
            <Button variant="primary" shape="pill" onClick={modalCtl.actions.openCreate}>
                Add New
            </Button>
        </>
    );

    const { open, mode, entity, values } = modalCtl.modal;
    const displayName =
        entity ? `${entity.indicator ?? ""} — ${entity.geoUnit ?? ""} — ${entity.date ?? ""}`.trim() : undefined;
    const copy = useDialogCopy({
        entityLabel: "Indicator Data",
        mode,
        entity: displayName ? { name: displayName } : undefined,
    });
    const fields = useMemo(() => (mode === "delete" ? [] : indicatorDataFields), [mode, indicatorDataFields]);

    return (
        <>
            <GenericListView
                title="Indicator Data"
                subtitle="View, search, filter and add the different indicators data available in the system"
                actions={actions}
                header={header}
                columns={columns}
                tableProps={controller.tableProps}
                paginationProps={controller.paginationProps}
                actionCell={actionCell}
                actionHeader="Actions"
            />
            <ModalContainer
                open={open}
                mode={mode}
                title={copy.title}
                subtitle={copy.subtitle}
                yesLabel={copy.yesLabel}
                yesVariant={copy.yesVariant}
                fields={fields}
                values={values}
                setValue={modalCtl.actions.setValue}
                onNo={modalCtl.actions.close}
                onYes={modalCtl.actions.commit}
            />
        </>
    );
}
